import {
  User,
  UserOrganizationMembership,
} from '../types/onboarding';

export const ACTIVE_ORG_SESSION_KEY = 'rpt.activeOrganizationId';
export const ACTIVE_ORG_QUERY_PARAM = 'active_organization_id';
export const WORKSPACE_INVALIDATED_EVENT = 'rpt:workspace-invalidated';

export interface WorkspaceInvalidatedDetail {
  removedOrganizationId?: number;
}

/**
 * True only when the tab workspace (active_organization_id) is invalid — not
 * cross-org permission denials on a specific resource.
 */
export function isWorkspaceForbiddenError(
  status: number,
  message: string
): boolean {
  if (status !== 403) return false;
  const lower = message.toLowerCase();
  return (
    lower.includes('no membership in requested organization') ||
    lower.includes('no membership in active organization') ||
    lower.includes('no active organization selected')
  );
}

export function dispatchWorkspaceInvalidated(
  detail?: WorkspaceInvalidatedDetail
): void {
  window.dispatchEvent(
    new CustomEvent(WORKSPACE_INVALIDATED_EVENT, { detail: detail ?? null })
  );
}

export function getSessionActiveOrganizationId(): number | null {
  const raw = sessionStorage.getItem(ACTIVE_ORG_SESSION_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function setSessionActiveOrganizationId(organizationId: number): void {
  sessionStorage.setItem(ACTIVE_ORG_SESSION_KEY, String(organizationId));
}

export function clearSessionActiveOrganizationId(): void {
  sessionStorage.removeItem(ACTIVE_ORG_SESSION_KEY);
}

/** Pick tab workspace org: session value, else DB default, else first membership. */
export function resolveSessionActiveOrganizationId(
  memberships: UserOrganizationMembership[],
  dbDefaultOrganizationId: number | null | undefined
): number | null {
  const membershipIds = new Set(
    memberships.map((membership) => membership.organizationId)
  );
  const stored = getSessionActiveOrganizationId();

  if (stored != null && membershipIds.has(stored)) {
    return stored;
  }

  if (
    dbDefaultOrganizationId != null &&
    membershipIds.has(dbDefaultOrganizationId)
  ) {
    setSessionActiveOrganizationId(dbDefaultOrganizationId);
    return dbDefaultOrganizationId;
  }

  if (memberships.length > 0) {
    const firstId = memberships[0].organizationId;
    setSessionActiveOrganizationId(firstId);
    return firstId;
  }

  clearSessionActiveOrganizationId();
  return null;
}

export function applyActiveOrganizationToUser(
  user: User,
  memberships: UserOrganizationMembership[],
  organizationId: number | null
): User {
  if (user.isPlatformAdmin || organizationId == null) {
    return user;
  }

  const membership = memberships.find(
    (entry) => entry.organizationId === organizationId
  );
  if (!membership) {
    return user;
  }

  return {
    ...user,
    activeOrganizationId: organizationId,
    organization: membership.organizationName,
    platformRole: membership.orgRole,
  };
}
